/**
 * UserForm Component
 *
 * Simplified form for creating tenant users with TAP-only authentication.
 * Fields: UPN prefix + domain, Display name, TAP configuration, Mobile phone, Account enabled
 */

import React, { useState, useEffect } from 'react';
import {
  Input,
  Label,
  Switch,
  Checkbox,
  Dropdown,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PhoneInput } from 'react-international-phone';
import { parsePhoneNumber } from 'libphonenumber-js';
import 'react-international-phone/style.css';
import type { TenantUser } from '../../types/user';
import type { UserRole } from '../../types/auth';
import { canEditRole, getAssignableRoles, getRoleDisplayText } from './rolePermissions';

const TAP_LIFETIME_OPTIONS = [
  { label: '1 hour', value: 60 },
  { label: '2 hours', value: 120 },
  { label: '3 hours', value: 180 },
  { label: '4 hours', value: 240 },
  { label: '5 hours', value: 300 },
  { label: '6 hours', value: 360 },
  { label: '7 hours', value: 420 },
  { label: '8 hours', value: 480 },
];

interface UserFormData {
  upnPrefix: string;
  displayName: string;
  accountEnabled: boolean;
  sendViaEmail: boolean;
  personalEmail: string;
  mobilePhone: string; // REQUIRED: Mobile phone for TAP delivery
  // TAP (Temporary Access Pass) configuration - TAP is the only authentication method
  use_tap: boolean; // Always true - TAP is the only authentication method
  tap_lifetime_minutes: number; // Default: 60 (1 hour), options: 1-8 hours in 1-hour increments
  tap_one_time_use: boolean; // Default: true - single-use TAP
  role?: string; // User role (edit mode only)
}

interface UserFormProps {
  mode: 'add' | 'edit';
  initialData?: TenantUser;
  tenantDomain: string;
  onValidationChange: (valid: boolean, errors: string[]) => void;
  onChange: (data: Partial<UserFormData>, hasChanges: boolean) => void;
  disabled?: boolean;
  /** Current user's role - used to determine role editing permissions */
  currentUserRole?: UserRole;
  /** True if in organization context (OrganizationDetailPage), false for tenant context (regular Users page) */
  isOrganizationContext?: boolean;
}

const useStyles = makeStyles({
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '20px',
  },
  upnContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  upnPrefix: {
    flex: '0 0 200px',
  },
  upnDomain: {
    flex: '1',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '12px',
    marginTop: '4px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '12px',
    color: tokens.colorNeutralForeground1,
  },
  phoneInput: {
    '& .react-international-phone-input-container': {
      width: '100%',
    },
    '& .react-international-phone-input': {
      width: '100%',
      fontFamily: tokens.fontFamilyBase,
      fontSize: tokens.fontSizeBase300,
      padding: tokens.spacingVerticalS,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRadius: tokens.borderRadiusMedium,
      '&:focus': {
        outline: `2px solid ${tokens.colorBrandStroke1}`,
        outlineOffset: '2px',
      },
    },
    '& .react-international-phone-country-selector-button': {
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRadius: tokens.borderRadiusMedium,
      marginRight: tokens.spacingHorizontalS,
    },
  },
});

export const UserForm: React.FC<UserFormProps> = ({
  mode,
  initialData,
  tenantDomain,
  onValidationChange,
  onChange,
  disabled = false,
  currentUserRole,
  isOrganizationContext = true,
}) => {
  const styles = useStyles();

  // Extract UPN prefix from existing user if in edit mode
  const getUpnPrefix = () => {
    if (initialData?.user_principal_name) {
      return initialData.user_principal_name.split('@')[0] || '';
    }
    return '';
  };

  const [formData, setFormData] = useState<UserFormData>({
    upnPrefix: getUpnPrefix(),
    displayName: initialData?.display_name || '',
    accountEnabled: initialData?.account_enabled ?? true,
    sendViaEmail: false,
    personalEmail: '',
    mobilePhone: '',
    // TAP defaults - TAP is the only authentication method
    use_tap: true, // TAP is the only authentication method
    tap_lifetime_minutes: 60, // Default 60 minutes
    tap_one_time_use: true, // Default to one-time use
    role: initialData?.role ?? undefined, // Role from initial data (edit mode)
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [initialFormData] = useState<UserFormData>(formData);
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = useState(false);

  // Auto-populate display name from UPN prefix (add mode only)
  useEffect(() => {
    if (mode === 'add' && !displayNameManuallyEdited && formData.upnPrefix) {
      setFormData(prev => ({ ...prev, displayName: formData.upnPrefix }));
    }
  }, [formData.upnPrefix, displayNameManuallyEdited, mode]);

  // Validate UPN prefix pattern
  const validateUpnPrefix = (prefix: string): boolean => {
    const upnPrefixRegex = /^[a-zA-Z0-9._-]+$/;
    return upnPrefixRegex.test(prefix);
  };

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Validate phone format using libphonenumber
  const validatePhone = (phone: string): boolean => {
    if (!phone) return false;
    try {
      const parsed = parsePhoneNumber(phone);
      return parsed ? parsed.isValid() : false;
    } catch {
      return false;
    }
  };

  // Validate form and update errors
  useEffect(() => {
    const newErrors: Record<string, string> = {};

    // UPN prefix validation (only for create mode)
    if (mode === 'add') {
      if (!formData.upnPrefix.trim()) {
        newErrors.upnPrefix = 'User principal name prefix is required';
      } else if (!validateUpnPrefix(formData.upnPrefix)) {
        newErrors.upnPrefix = 'Only letters, numbers, periods, hyphens, and underscores allowed';
      }
    }

    // Display name validation
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    // Communication validation (only for create mode)
    if (mode === 'add') {
      // Email validation when sendViaEmail is checked
      if (formData.sendViaEmail) {
        if (!formData.personalEmail.trim()) {
          newErrors.personalEmail = 'Personal email is required for email delivery';
        } else if (!validateEmail(formData.personalEmail)) {
          newErrors.personalEmail = 'Invalid email format';
        }
      }

      // Phone number always required for TAP delivery
      if (!formData.mobilePhone.trim()) {
        newErrors.mobilePhone = 'Mobile phone number is required';
      } else if (!validatePhone(formData.mobilePhone)) {
        newErrors.mobilePhone = 'Invalid phone number for selected country';
      }
    }

    setErrors(newErrors);

    // Notify parent of validation status
    const isValid = Object.keys(newErrors).length === 0;
    const errorMessages = Object.values(newErrors);
    onValidationChange(isValid, errorMessages);

    // Notify parent of changes
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    onChange(formData, hasChanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleFieldChange = (field: keyof UserFormData, value: string | boolean | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDisplayNameChange = (value: string) => {
    setFormData(prev => ({ ...prev, displayName: value }));

    // If user clears the field, reset to auto-sync mode
    if (value === '') {
      setDisplayNameManuallyEdited(false);
    }
    // If value is different from UPN prefix, mark as manually edited
    else if (value !== formData.upnPrefix) {
      setDisplayNameManuallyEdited(true);
    }
  };

  const handleBlur = (field: keyof UserFormData) => {
    setTouched(prev => ({
      ...prev,
      [field]: true,
    }));
  };

  return (
    <div>
      {/* Identity Section */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Identity</div>

        {/* User Principal Name */}
        <div className={styles.formField}>
          <Label htmlFor="upnPrefix" required>
            User principal name
          </Label>
          <div className={styles.upnContainer}>
            <Input
              id="upnPrefix"
              className={styles.upnPrefix}
              value={formData.upnPrefix}
              onChange={(_ev, data) => handleFieldChange('upnPrefix', data.value)}
              onBlur={() => handleBlur('upnPrefix')}
              disabled={disabled || mode === 'edit'} // Can't change UPN in edit mode
              aria-invalid={touched.upnPrefix && !!errors.upnPrefix}
              placeholder="username"
            />
            <span>@</span>
            <Input
              className={styles.upnDomain}
              value={tenantDomain}
              disabled
              readOnly
            />
          </div>
          {touched.upnPrefix && errors.upnPrefix && (
            <div className={styles.errorMessage}>{errors.upnPrefix}</div>
          )}
        </div>

        {/* Display Name */}
        <div className={styles.formField}>
          <Label htmlFor="displayName" required>
            Display name
          </Label>
          <Input
            id="displayName"
            value={formData.displayName}
            onChange={(_ev, data) => handleDisplayNameChange(data.value)}
            onBlur={() => handleBlur('displayName')}
            disabled={disabled}
            aria-invalid={touched.displayName && !!errors.displayName}
          />
          {touched.displayName && errors.displayName && (
            <div className={styles.errorMessage}>{errors.displayName}</div>
          )}
        </div>
      </div>

      {/* TAP Configuration Section (Create mode only) - TAP is the only authentication method */}
      {mode === 'add' && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>TAP Configuration</div>

          {/* TAP Lifetime Dropdown */}
          <div className={styles.formField}>
            <Label htmlFor="tapLifetime" id="tapLifetimeLabel">
              TAP Lifetime
            </Label>
            <Dropdown
              id="tapLifetime"
              aria-labelledby="tapLifetimeLabel"
              value={TAP_LIFETIME_OPTIONS.find(opt => opt.value === formData.tap_lifetime_minutes)?.label || '1 hour'}
              selectedOptions={[String(formData.tap_lifetime_minutes)]}
              onOptionSelect={(_ev, data) => {
                const selectedValue = Number(data.optionValue);
                handleFieldChange('tap_lifetime_minutes', selectedValue);
              }}
              disabled={disabled}
              style={{ width: 'fit-content', minWidth: '120px' }}
            >
              {TAP_LIFETIME_OPTIONS.map(option => (
                <Option key={option.value} value={String(option.value)}>
                  {option.label}
                </Option>
              ))}
            </Dropdown>
          </div>

          {/* One-time Use Checkbox */}
          <div className={styles.formField}>
            <Checkbox
              label="One-time use only"
              checked={formData.tap_one_time_use}
              onChange={(_ev, data) => handleFieldChange('tap_one_time_use', data.checked === true)}
              disabled={disabled}
            />
          </div>
        </div>
      )}

      {/* Communication Section (Create mode only) */}
      {mode === 'add' && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Communication</div>

          {/* Send Via Email Checkbox */}
          <div className={styles.formField}>
            <Checkbox
              label="Send credentials via Email"
              checked={formData.sendViaEmail}
              onChange={(_ev, data) => handleFieldChange('sendViaEmail', data.checked === true)}
              disabled={disabled}
            />
          </div>

          {/* Personal Email (shown when sendViaEmail checked) */}
          {formData.sendViaEmail && (
            <div className={styles.formField}>
              <Label htmlFor="personalEmail" required>
                Personal email
              </Label>
              <Input
                id="personalEmail"
                type="email"
                value={formData.personalEmail}
                onChange={(_ev, data) => handleFieldChange('personalEmail', data.value)}
                onBlur={() => handleBlur('personalEmail')}
                disabled={disabled}
                aria-invalid={touched.personalEmail && !!errors.personalEmail}
                placeholder="user@example.com"
              />
              {touched.personalEmail && errors.personalEmail && (
                <div className={styles.errorMessage}>{errors.personalEmail}</div>
              )}
            </div>
          )}

          {/* Mobile Phone (always required for TAP delivery) */}
          <div className={styles.formField}>
            <Label htmlFor="mobilePhone" required>
              Mobile phone
            </Label>
            <div className={styles.phoneInput}>
              <PhoneInput
                defaultCountry="us"
                value={formData.mobilePhone}
                onChange={(phone) => handleFieldChange('mobilePhone', phone)}
                onBlur={() => handleBlur('mobilePhone')}
                disabled={disabled}
                inputProps={{
                  id: 'mobilePhone',
                  'aria-invalid': touched.mobilePhone && !!errors.mobilePhone,
                  'data-testid': 'contact-phone-input',
                  required: true,
                } as React.InputHTMLAttributes<HTMLInputElement> & { 'data-testid'?: string }}
                style={{
                  width: '100%',
                }}
              />
            </div>
            {touched.mobilePhone && errors.mobilePhone && (
              <div className={styles.errorMessage}>{errors.mobilePhone}</div>
            )}
          </div>
        </div>
      )}

      {/* Account Section */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Account</div>

        <div className={styles.formField}>
          <Switch
            id="accountEnabled"
            label="Account enabled"
            checked={formData.accountEnabled}
            onChange={(_ev, data) => handleFieldChange('accountEnabled', data.checked)}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Role Section (Edit mode only) */}
      {mode === 'edit' && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Role</div>

          <div className={styles.formField}>
            {canEditRole(currentUserRole, initialData?.role ?? undefined) ? (
              <>
                <Label htmlFor="roleSelector" id="roleSelectorLabel">
                  Role
                </Label>
                <Dropdown
                  id="roleSelector"
                  aria-labelledby="roleSelectorLabel"
                  value={getRoleDisplayText(formData.role)}
                  selectedOptions={formData.role ? [formData.role] : []}
                  onOptionSelect={(_ev, data) => {
                    const selectedRole = data.optionValue;
                    if (selectedRole) {
                      handleFieldChange('role', selectedRole);
                    }
                  }}
                  disabled={disabled}
                  style={{ width: '180px' }}
                >
                  {getAssignableRoles(currentUserRole, isOrganizationContext).map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Dropdown>
              </>
            ) : (
              <>
                <Label>Current Role</Label>
                <div style={{ padding: '8px 0' }}>
                  {getRoleDisplayText(initialData?.role)}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
